'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import type { SectionPhase } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/types';
interface SectionExitOptions {
  phase: SectionPhase;
  onExitEnd: () => void;
  // How long this slide takes to hand over, in seconds.
  durationSeconds: number;
}

/**
 * THE DEFAULT WAY TO LEAVE: wait, then report.
 *
 * A slide with a bespoke exit animation should NOT use this -- it should
 * call `onExitEnd` when its own animation finishes, which is the whole
 * point of the protocol. This is for the slides that have nothing of their
 * own to play and only need to hold the hand-over open long enough for the
 * page's atmosphere (see SectionPhase) to clear.
 *
 * REPORTS EXACTLY ONCE PER EXIT. The latch matters because `onExitEnd` is
 * a fresh identity on every render of the parent: without it, any re-render
 * during the exit window would restart the timer and the hand-over would
 * never land.
 */
export const useSectionExit = t0 => {
  const $ = _c(6);
  const {
    phase,
    onExitEnd,
    durationSeconds
  } = t0;
  const exitEndRef = useRef(onExitEnd);
  let t1;
  if ($[0] !== onExitEnd) {
    t1 = () => {
      exitEndRef.current = onExitEnd;
    };
    $[0] = onExitEnd;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useEffect(t1);
  let t2;
  let t3;
  if ($[2] !== durationSeconds || $[3] !== phase) {
    t2 = () => {
      if (phase !== "exiting") {
        return;
      }
      const timer = window.setTimeout(() => exitEndRef.current(), durationSeconds * 1000);
      return () => window.clearTimeout(timer);
    };
    t3 = [phase, durationSeconds];
    $[2] = durationSeconds;
    $[3] = phase;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
};